import { useState } from "react";
import { Search, Monitor } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GlassCard } from "@/components/ui/glass-card";
import { AppleDeviceIcon } from "@/components/icons";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";

interface TagDevicesTabProps { tagId: string; tagName?: string; }

export const TagDevicesTab = ({ tagId, tagName }: TagDevicesTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ["tag-devices", tagId],
    queryFn: () => tagsApi.getDevicesByTag(tagId),
  });

  const filteredDevices = devices.filter((d: any) =>
    (d.name || d.deviceName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (d.hostname || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <GlassCard key={i} variant="subtle" hover={false} className="p-4 animate-pulse">
            <div className="flex items-center space-x-3 mb-3"><div className="w-8 h-8 rounded-lg bg-muted"></div><div className="flex-1 space-y-1"><div className="h-4 bg-muted rounded w-3/4"></div><div className="h-3 bg-muted rounded w-1/2"></div></div></div>
          </GlassCard>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="Search devices..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9" />
        </div>
      </div>

      {filteredDevices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Monitor className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">No Devices Found</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {searchQuery ? "No devices match your search criteria." : "This tag has no devices assigned to it yet."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDevices.map((device: any) => (
            <GlassCard key={device.id || device.deviceId} variant="subtle" className="p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <AppleDeviceIcon osType={device.osType || device.os_type || 'macOS'} modelName={device.name || device.deviceName} className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/devices/${device.id || device.deviceId}?from=tag&tagId=${tagId}&tagName=${encodeURIComponent(tagName || 'Tag')}`}
                    className="font-medium text-sm hover:text-primary transition-colors block truncate">
                    {device.name || device.deviceName}
                  </Link>
                  {device.hostname && <p className="text-xs text-muted-foreground truncate">{device.hostname}</p>}
                </div>
              </div>
              <div className="space-y-1.5 text-xs">
                {(device.osType || device.os_type) && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">OS</span>
                    <span className="font-medium">{device.osType || device.os_type} {device.osVersion || device.os_version}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={device.status === 'online' ? 'default' : device.status === 'offline' ? 'destructive' : 'secondary'} className="text-xs h-5">
                    {device.status || 'unknown'}
                  </Badge>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};
